import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { login } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/uiSlice';
import { FiLogIn, FiAlertCircle } from 'react-icons/fi';

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const handleLogin = async () => {
    try {
      await dispatch(login()).unwrap();
      
      dispatch(
        addNotification({
          type: 'success',
          title: 'Login Successful',
          message: 'You have successfully logged in.',
        })
      );
      
      navigate('/dashboard');
    } catch (error: any) {
      dispatch(
        addNotification({
          type: 'error',
          title: 'Login Failed',
          message: error.message || 'Failed to login. Please try again.',
        })
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Sign in to your account
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Access the tenant management portal
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300 flex items-center">
          <FiAlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <FiLogIn className="mr-2" />
        )}
        {loading ? 'Signing in...' : 'Sign in with Azure AD'}
      </button>

      <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
        <p>
          By continuing, you agree to our <span className="text-primary-600 dark:text-primary-400">Terms of Service</span> and <span className="text-primary-600 dark:text-primary-400">Privacy Policy</span>.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;